import InsightCard from './InsightCard';
import OpportunityItem from './OpportunityItem';
import type { Insight } from './InsightCard';
import type { Opportunity } from './OpportunityItem';

interface InsightData {
  insights: Insight[];
  opportunities: Opportunity[];
}

interface InsightPanelProps {
  data: InsightData;
  onAction: (prompt: string) => void;
}

export type { InsightData };

export function parseInsightBlocks(content: string): { text: string; insightBlocks: InsightData[] } {
  const regex = /```json:insights\s*\n([\s\S]*?)```/g;
  const insightBlocks: InsightData[] = [];
  let match;

  while ((match = regex.exec(content)) !== null) {
    try {
      const parsed = JSON.parse(match[1]);
      const block: InsightData = {
        insights: [],
        opportunities: [],
      };
      const validSeverities = new Set(['critical', 'warning', 'opportunity']);
      if (Array.isArray(parsed.insights)) {
        block.insights = parsed.insights
          .filter(
            (i: Record<string, unknown>) =>
              i &&
              typeof i.title === 'string' &&
              typeof i.severity === 'string' &&
              validSeverities.has(i.severity as string) &&
              typeof i.description === 'string'
          )
          .slice(0, 5);
      }
      if (Array.isArray(parsed.opportunities)) {
        block.opportunities = parsed.opportunities
          .filter(
            (o: Record<string, unknown>) =>
              o &&
              typeof o.title === 'string' &&
              typeof o.impact === 'string'
          )
          .slice(0, 4);
      }
      if (block.insights.length > 0 || block.opportunities.length > 0) {
        insightBlocks.push(block);
      }
    } catch {}
  }

  const text = content.replace(regex, '').trim();

  return { text, insightBlocks };
}

export default function InsightPanel({ data, onAction }: InsightPanelProps) {
  if (data.insights.length === 0 && data.opportunities.length === 0) return null;

  return (
    <div className="insight-panel">
      {data.insights.length > 0 && (
        <div className="insight-panel-section">
          <div className="insight-panel-section-title">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Issues Found
          </div>
          {data.insights.map((insight, idx) => (
            <InsightCard key={idx} insight={insight} onAction={onAction} />
          ))}
        </div>
      )}
      {data.opportunities.length > 0 && (
        <div className="insight-panel-section">
          <div className="insight-panel-section-title">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Opportunities
          </div>
          {data.opportunities.map((opp, idx) => (
            <OpportunityItem key={idx} opportunity={opp} onAction={onAction} />
          ))}
        </div>
      )}
    </div>
  );
}
